import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './CustomListRecordPage.scss';
import EditCustomListRecord from './EditCustomListRecord';
import ViewCustomListRecord from './ViewCustomListRecord';
import { customListApi, customListRecordApi, customListSchemaApi } from '../../apis';
import { useNavigate, useParams } from 'react-router';
import {
  InternalApiCustomList,
  InternalApiCustomListRecord,
  InternalApiCustomListSchemaField,
  InternalApiCustomListSchemaFieldType,
} from '../../api-internal';
import { Spinner } from '../../feedback/Spinner';
import { groupFields } from './helper';
import Has from '../../auth/Has';
import {
  CAN_ADD_CUSTOM_LIST_RECORD,
  CAN_MODIFY_CUSTOM_LIST_RECORD,
  CAN_READ_CUSTOM_LIST_RECORD,
} from '../../auth/permissions';

type RecordGroupKey = {
  type: InternalApiCustomListSchemaFieldType;
  isObject: boolean;
  isMulti: boolean;
};

export type RecordGroup = {
  key: RecordGroupKey;
  items: InternalApiCustomListSchemaField[];
};

enum CustomListRecordPageState {
  LOADING,
  NEW_RECORD,
  EDIT_RECORD,
  VIEW_RECORD,
}

const CustomListRecordPage = () => {
  const navigate = useNavigate();
  const { customListId, recordId } = useParams<{
    customListId: string;
    recordId: string | undefined;
  }>();
  const [customList, setCustomList] = useState<InternalApiCustomList>();
  const [schemaFields, setSchemaFields] = useState<InternalApiCustomListSchemaField[]>([]);
  const [record, setRecord] = useState<InternalApiCustomListRecord>();
  const [pageState, setPageState] = useState<CustomListRecordPageState>(CustomListRecordPageState.LOADING);

  const getHistoricSchema = useCallback(
    (customListId: string, schemaId: number) => {
      customListSchemaApi
        .getListHistoricSchema({
          customListId: customListId,
          schemaId: schemaId,
        })
        .then((result) => {
          setSchemaFields(result.schemaFields.sort((a, b) => a.sortOrder - b.sortOrder));
        })
        .catch(() => navigate('/custom-lists/' + customListId));
    },
    [navigate]
  );

  const getActiveSchema = useCallback(
    (customListId: string) => {
      customListSchemaApi
        .getCustomListSchema({ customListId: customListId! })
        .then((result) => {
          setSchemaFields(result.schemaFields.sort((a, b) => a.sortOrder - b.sortOrder));
        })
        .catch(() => navigate('/custom-lists'));
    },
    [navigate]
  );

  useEffect(() => {
    const requests = [
      customListApi
        .getListById({ customListId: customListId! })
        .then((result) => setCustomList(result))
        .catch(() => navigate('/custom-lists')),
    ];

    if (recordId) {
      requests.push(
        customListRecordApi
          .getRecord({
            customListId: customListId!,
            id: recordId!,
          })
          .then((result) => {
            setRecord(result);
            if (result.historicSchemaId) {
              getHistoricSchema(customListId!, result.historicSchemaId);
            } else {
              getActiveSchema(customListId!);
            }
          })
          .catch(() => navigate('/custom-lists/' + customListId))
      );
    } else {
      requests.push(
        new Promise<void>((resolve) => {
          getActiveSchema(customListId!);
          resolve();
        })
      );
    }

    Promise.all(requests).finally(() => {
      if (!recordId) {
        setPageState(CustomListRecordPageState.NEW_RECORD);
      } else {
        setPageState(CustomListRecordPageState.VIEW_RECORD);
      }
    });
  }, [customListId, recordId]);

  const groupedFields = useMemo(() => {
    return groupFields(schemaFields);
  }, [schemaFields]);

  const Page = useCallback(() => {
    switch (pageState) {
      case CustomListRecordPageState.NEW_RECORD:
        return (
          <Has allPermissions={[CAN_ADD_CUSTOM_LIST_RECORD]}>
            <EditCustomListRecord
              isNewRecord={true}
              customList={customList}
              schemaFields={schemaFields}
              record={record}
              toggle={() => {}}
              groupedFields={groupedFields}
            />
          </Has>
        );
      case CustomListRecordPageState.EDIT_RECORD:
        return (
          <Has allPermissions={[CAN_MODIFY_CUSTOM_LIST_RECORD]}>
            <EditCustomListRecord
              isNewRecord={false}
              customList={customList}
              schemaFields={schemaFields}
              record={record}
              setRecord={setRecord}
              toggle={() => setPageState(CustomListRecordPageState.VIEW_RECORD)}
              groupedFields={groupedFields}
            />
          </Has>
        );
      case CustomListRecordPageState.VIEW_RECORD:
        return (
          <Has allPermissions={[CAN_READ_CUSTOM_LIST_RECORD]}>
            <ViewCustomListRecord
              customList={customList}
              schemaFields={schemaFields}
              record={record}
              toggle={() => setPageState(CustomListRecordPageState.EDIT_RECORD)}
              groupedFields={groupedFields}
            />
          </Has>
        );
      case CustomListRecordPageState.LOADING:
      default:
        return <Spinner loading={true} />;
    }
  }, [pageState, groupedFields]);

  return <Page />;
};
export default CustomListRecordPage;
